import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import CustomerNoteComponent from '@/app/(app)/customers/[id]/customer-note';
import { useCustomerCtx } from '@/app/(app)/customers/[id]/provider';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

type NoteCreate = ApiSchemas['NoteCreate'];
type NoteVisibility = ApiSchemas['NoteVisibility'];

interface FilterState {
  visibility: string[];
  category: string[]; // Placeholder for future category filtering
}

const VISIBILITY_OPTIONS = [
  { value: 'INTERNAL', label: 'Internal' },
  { value: 'EXTERNAL', label: 'External' },
];

const CATEGORY_OPTIONS: Array<{ value: string; label: string }> = [
  // Placeholder for future categories
];

export default function CustomerNotes() {
  const { api } = useApiV1Ctx();
  const { officeId } = useOfficeCtx();
  const { customer, refetch } = useCustomerCtx();
  const [filters, setFilters] = useState<FilterState>({
    visibility: [],
    category: [],
  });
  const [isCreating, setIsCreating] = useState(false);

  const [pinnedNotes, unpinnedNotes] = useMemo(() => {
    const filtered = customer.notes.filter((note) => {
      if (filters.visibility.length > 0 && !filters.visibility.includes(note.visibility)) {
        return false;
      }
      // Category filtering placeholder - can be expanded when categories are implemented
      return true;
    });
    // Sort by created_at
    filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    // Group by pinned status
    const pinned = filtered.filter((note) => note.pinned_at);
    const unpinned = filtered.filter((note) => !note.pinned_at);
    return [pinned, unpinned];
  }, [customer.notes, filters]);

  // Create note mutation
  const createMutation = api.notes.createNotesPost.useMutation(
    {},
    {
      onSuccess: () => {
        toast.success('Note created successfully');
        setIsCreating(false);
        refetch();
      },
    }
  );

  const pinMutation = api.notes.pinNotesNoteIdPinPost.useMutation(undefined, {
    onSuccess: () => {
      refetch();
    },
  });

  const deleteMutation = api.notes.removeNotesNoteIdDelete.useMutation(undefined, {
    onSuccess: () => {
      toast.success('Note deleted successfully');
      refetch();
    },
  });

  const handleCreateNote = (noteData: { content: string; visibility: NoteVisibility }) => {
    const createData: NoteCreate = {
      office_id: officeId,
      visibility: noteData.visibility,
      linked_to_type: 'CUSTOMER',
      linked_to_id: customer.id,
      customer_id: customer.id,
      content: noteData.content,
    };
    createMutation.mutate(createData);
  };

  const handlePinNote = (noteId: string, pinned: boolean) => {
    pinMutation.mutate({ path: { note_id: noteId }, query: { pinned } });
  };

  const handleDeleteNote = (noteId: string) => {
    if (confirm('Are you sure you want to delete this note?')) {
      deleteMutation.mutate({ path: { note_id: noteId } });
    }
  };

  return (
    <div className='flex flex-col gap-4'>
      {pinnedNotes.length > 0 && (
        <div className='space-y-4'>
          <h2 className='text-heading-sm'>Pinned</h2>
          <div className='border-t-[0.5px]'>
            {pinnedNotes.map((note) => (
              <CustomerNoteComponent
                key={note.id}
                note={note}
                onPin={handlePinNote}
                onDelete={handleDeleteNote}
                isDeleting={deleteMutation.isPending}
                isPinning={pinMutation.isPending}
              />
            ))}
          </div>
        </div>
      )}
      {unpinnedNotes.length > 0 && (
        <div className='space-y-4'>
          <h2 className='text-heading-sm'>General Notes</h2>
          <div className='border-t-[0.5px]'>
            {unpinnedNotes.map((note) => (
              <CustomerNoteComponent
                key={note.id}
                note={note}
                onPin={handlePinNote}
                onDelete={handleDeleteNote}
                isDeleting={deleteMutation.isPending}
                isPinning={pinMutation.isPending}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface CreateNoteFormProps {
  onSubmit: (data: { content: string; visibility: NoteVisibility }) => void;
  onCancel: () => void;
  isLoading: boolean;
}

function CreateNoteForm({ onSubmit, onCancel, isLoading }: CreateNoteFormProps) {
  const [content, setContent] = useState('');
  const [visibility, setVisibility] = useState<NoteVisibility>('INTERNAL');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (content.trim()) {
      onSubmit({ content: content.trim(), visibility });
    }
  };

  return (
    <Card className='border-dashed'>
      <CardHeader>
        <CardTitle>Create New Note</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className='space-y-4'>
          <Select value={visibility} onValueChange={(value) => setVisibility(value as NoteVisibility)}>
            <SelectTrigger className='col-span-2'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value='INTERNAL'>Internal</SelectItem>
                <SelectItem value='EXTERNAL'>External</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          <Textarea
            className='col-span-2'
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder='Enter note content...'
            rows={4}
            required
          />
          <div className='flex justify-end gap-2'>
            <Button type='button' variant='outline' onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button type='submit' disabled={!content.trim() || isLoading}>
              {isLoading ? 'Creating...' : 'Create Note'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
